import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import { listDatasetsPage, deleteDataset } from 'features/datasets/api/datasetsApi';
import NewDatasetDialog from 'features/datasets/components/NewDatasetDialog';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import BulkBar from 'shared/components/tables/BulkBar';
import TableFooter from 'shared/components/tables/TableFooter';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import {
  activitySearchWrapperClass, activitySearchInputClass, tableHeaderRowClass,
  tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

export const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;

export function timeAgo(dateStr) {
  if (!dateStr) return '-';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(dateStr)) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24); if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7); if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30); if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Header labels, shared by the table and its loading skeleton. */
const DATASET_COLUMNS = ['Name', 'Description', 'Rows', 'Updated'];

/**
 * The datasets list: search on the left, New dataset on the right, a table
 * whose row opens the dataset (Name · Description · Rows · Updated). Names
 * are searched and rows are paged on the server; ticking rows brings up
 * Delete.
 */
export default function DatasetsListPage() {
  useBreadcrumb([{ label: 'Datasets' }]);
  const { confirm: appConfirm } = useAppDialog();
  const { team } = useParams();
  const navigate = useNavigate();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  // The search box feeds the server after a pause, not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setTerm(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  /** Fetch a page; the newest request wins, so a slow answer to an old search is dropped. */
  const load = useCallback(async ({ offset = 0 } = {}) => {
    const mine = ++seq.current;
    if (offset === 0) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const page = await listDatasetsPage({ limit: PAGE_SIZE, offset, q: term });
      if (mine !== seq.current) return;
      setRows((cur) => (offset === 0 ? page.datasets : [...cur, ...page.datasets]));
      setTotal(page.total);
      setLoaded(true);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e?.response?.data?.error || e.message || 'Failed to load datasets');
    } finally {
      if (mine === seq.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [term]);
  useEffect(() => { load(); }, [load]);

  // A reload or a new search can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => new Set([...cur].filter((id) => rows.some((r) => r.id === id)))); }, [rows]);

  // "New dataset" asks for a name and opens the empty dataset; rows come on its page.
  const [creating, setCreating] = useState(false);
  const handleCreate = () => setCreating(true);

  // Only rows the caller may delete can be ticked.
  const deletable = rows.filter((r) => r.can_delete);
  const chosen = rows.filter((r) => picked.has(r.id) && r.can_delete);
  const allPicked = deletable.length > 0 && deletable.every((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const removeChosen = async () => {
    if (!chosen.length) return;
    const what = chosen.length === 1 ? `"${chosen[0].name}" and its items` : `${plural(chosen.length, 'dataset', 'datasets')} and their items`;
    const ok = await appConfirm(`${what} will be deleted.`, {
      title: chosen.length === 1 ? 'Delete dataset' : 'Delete datasets', confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const res = await Promise.allSettled(chosen.map((r) => deleteDataset(r.id)));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await load();
    if (failed.length) {
      const why = failed[0].reason?.response?.data?.message || failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed';
      setError(failed.length === chosen.length ? why : `${plural(failed.length, 'dataset', 'datasets')} could not be deleted: ${why}`);
    }
    setBusy(false);
  };

  const open = (id) => navigate(`/${encodeURIComponent(team)}/datasets/${id}`);

  return (
    <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`${activitySearchWrapperClass} w-[240px]`}>
            <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search datasets…" aria-label="Search datasets" className={activitySearchInputClass} />
            {query ? (
              <button type="button" onClick={() => setQuery('')} className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]" aria-label="Clear search">
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <div className="flex-1" />
          {canWrite && (
            <button type="button" onClick={handleCreate}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
              <Plus size={13} /> New dataset
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col px-4 pb-8 pt-3">
          {error && <div className="mb-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger">{error}</div>}
          {!loaded && loading ? (
            <PageLoader label="Loading datasets" />
          ) : (
            <>
              <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left">
                    <thead>
                      <tr className={tableHeaderRowClass}>
                        {canWrite && (
                          <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                            {deletable.length > 0 && (
                              <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'}
                                onChange={() => setPicked(allPicked ? new Set() : new Set(deletable.map((r) => r.id)))} />
                            )}
                          </th>
                        )}
                        {DATASET_COLUMNS.map((label) => (
                          <th key={label} scope="col" className={`${tableHeaderCellClass}${label === 'Name' || label === 'Description' ? '' : ' w-[130px]'}`}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr><td colSpan={DATASET_COLUMNS.length + (canWrite ? 1 : 0)} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                          {term ? `No datasets match "${term}"` : 'No datasets yet'}
                        </td></tr>
                      )}
                      {rows.map((d) => (
                        <tr key={d.id} className={`${tableRowClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}
                          tabIndex={0} aria-label={`Open ${d.name}`} onClick={() => open(d.id)}
                          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(d.id); } }}>
                          {canWrite && (
                            <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={picked.has(d.id)} disabled={!d.can_delete}
                                title={d.can_delete ? undefined : 'Only the creator, or a team owner or admin, can delete'}
                                aria-label={`Select ${d.name}`} onChange={() => toggleOne(d.id)} />
                            </td>
                          )}
                          <td className={`${tableDataCellClass} max-w-0`}><span className="block truncate font-medium">{d.name}</span></td>
                          <td className={`${tableDataCellClass} max-w-0 text-[color:var(--text-muted)]`}><span className="block truncate" title={d.description || undefined}>{d.description || '—'}</span></td>
                          <td className={`${tableDataCellClass} w-[130px] tabular-nums text-[color:var(--text-muted)]`}>{(Number(d.item_count) || 0).toLocaleString()}</td>
                          <td className={`${tableDataCellClass} w-[130px] text-[color:var(--text-muted)]`}>{timeAgo(d.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <TableFooter shown={rows.length} total={total} hasMore={rows.length < total}
                loading={loadingMore} onLoadMore={() => load({ offset: rows.length })} />
            </>
          )}
        </div>
      </div>
      <BulkBar count={canWrite ? picked.size : 0} label="Selected datasets" onClear={() => setPicked(new Set())} actions={[
        { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
      ]} />
      {creating ? <NewDatasetDialog onClose={() => setCreating(false)} onCreated={() => load({ offset: 0 })} /> : null}
    </main>
  );
}
